
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useValidation } from "@/contexts/ValidationContext";

export const useBusinessOnboarding = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { validateStep, clearErrors } = useValidation();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [businessData, setBusinessData] = useState({
    name: "",
    industry: "",
    website: "",
    brandVoice: "",
    description: "",
    keywords: [] as string[],
    colors: [] as string[]
  });

  const updateBusinessData = (key: string, value: string | string[]) => {
    setBusinessData(prev => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (!validateStep(step, businessData)) {
      return false;
    }
    
    if (step < 4) {
      setStep(step + 1);
      // Clear validation errors when moving to next step
      clearErrors();
      return true;
    }
    
    return false;
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      // Clear validation errors when moving back
      clearErrors();
      return true;
    }
    
    return false;
  };

  const handleSubmit = async () => {
    // Validate final step
    if (!validateStep(step, businessData)) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        throw new Error("You must be logged in to complete onboarding");
      }
      
      const { error } = await supabase
        .from('business')
        .insert([
          {
            owner_id: sessionData.session.user.id,
            name: businessData.name,
            industry: businessData.industry,
            website: businessData.website,
            brand_voice: businessData.brandVoice,
            description: businessData.description,
            is_onboarding_complete: true // Set onboarding as complete
          }
        ])
        .select()
        .single();
      
      if (error) throw error;
      
      toast({
        title: "Onboarding complete!",
        description: "Your business profile has been created successfully.",
      });
      
      navigate("/dashboard");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save business data",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    step,
    setStep,
    businessData,
    updateBusinessData,
    isSubmitting,
    handleSubmit,
    handleNext,
    handleBack
  };
};
